/**
 * Establishments API (list, search, export, classrooms, classes, contact-functions).
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type {
  Establishment,
  CreateEstablishmentDto,
  UpdateEstablishmentDto,
  EstablishmentsResponse,
  EstablishmentListParams,
  EstablishmentSearchResponse,
  Classroom,
  ContactFunction,
  SchoolClass,
} from '@/types/establishment.types';

/** Skip empty query values when building establishment list URLs. */
function appendQueryValue(
  params: URLSearchParams,
  key: string,
  value: string | number | boolean | undefined | null,
): void {
  if (value === undefined || value === null || value === '') return;
  params.append(key, String(value));
}

/** Repeat a query key once per list value. */
function appendQueryList(params: URLSearchParams, key: string, values?: string[]): void {
  values?.forEach((value) => params.append(key, value));
}

/** Flatten {@link EstablishmentListParams} onto list/export query strings. */
function appendEstablishmentFilters(
  queryParams: URLSearchParams,
  params?: EstablishmentListParams,
): void {
  appendQueryValue(queryParams, 'page', params?.page);
  appendQueryValue(queryParams, 'pageSize', params?.pageSize);
  appendQueryList(queryParams, 'types', params?.types);
  appendQueryList(queryParams, 'categories', params?.categories);
  appendQueryList(queryParams, 'priorityNetworks', params?.priorityNetworks);
  appendQueryValue(queryParams, 'isQpv', params?.isQpv);
  appendQueryList(queryParams, 'statuses', params?.statuses);
  appendQueryList(queryParams, 'prospectionStatuses', params?.prospectionStatuses);
  appendQueryList(queryParams, 'cities', params?.cities);
  appendQueryList(queryParams, 'departments', params?.departments);
  appendQueryValue(queryParams, 'search', params?.search);
  appendQueryValue(queryParams, 'coordinatorId', params?.coordinatorId);
  appendQueryValue(queryParams, 'managedById', params?.managedById);
  appendQueryValue(queryParams, 'isProspectionValidated', params?.isProspectionValidated);
  appendQueryList(queryParams, 'specialtyIds', params?.specialtyIds);
  appendQueryValue(queryParams, 'hasDevoirsFaits', params?.hasDevoirsFaits);
  appendQueryValue(queryParams, 'hasInternat', params?.hasInternat);
  appendQueryValue(queryParams, 'renewalNextYear', params?.renewalNextYear);
  appendQueryValue(queryParams, 'conventionSigned', params?.conventionSigned);
  appendQueryValue(queryParams, 'partnershipDateFrom', params?.partnershipDateFrom);
  appendQueryValue(queryParams, 'partnershipDateTo', params?.partnershipDateTo);
}

/** `GET /companies/current/establishments` — paginated list. */
export const getEstablishments = async (
  params?: EstablishmentListParams,
): Promise<EstablishmentsResponse> => {
  const queryParams = new URLSearchParams();
  appendEstablishmentFilters(queryParams, params);

  const queryString = queryParams.toString();
  const url = queryString
    ? `/companies/current/establishments?${queryString}`
    : '/companies/current/establishments';

  const { data } = await apiClient.get(url);
  return data;
};

/** `GET /companies/current/establishments/:id` */
export const getEstablishment = async (id: string): Promise<Establishment> => {
  const { data } = await apiClient.get(`/companies/current/establishments/${id}`);
  return data;
};

/** `POST /companies/current/establishments` */
export const createEstablishment = async (dto: CreateEstablishmentDto): Promise<Establishment> => {
  const { data } = await apiClient.post('/companies/current/establishments', dto);
  return data;
};

/** `PATCH /companies/current/establishments/:id` */
export const updateEstablishment = async (
  id: string,
  dto: UpdateEstablishmentDto,
): Promise<Establishment> => {
  const { data } = await apiClient.patch(`/companies/current/establishments/${id}`, dto);
  return data;
};

/** `DELETE /companies/current/establishments/:id` */
export const deleteEstablishment = async (id: string): Promise<void> => {
  await apiClient.delete(`/companies/current/establishments/${id}`);
};

/** `GET /companies/current/establishments/search` */
export const searchEstablishments = async (
  query: string,
  page: number = 1,
  limit: number = 5,
  department?: string,
  options?: { operationalOnly?: boolean; assignmentScope?: boolean },
  types?: string[],
): Promise<EstablishmentSearchResponse> => {
  const { data } = await apiClient.get<EstablishmentSearchResponse>(
    '/companies/current/establishments/search',
    {
      params: {
        query,
        page,
        limit,
        ...(department ? { department } : {}),
        ...(options?.operationalOnly ? { operationalOnly: true } : {}),
        ...(options?.assignmentScope ? { assignmentScope: true } : {}),
        ...(types && types.length > 0 ? { types } : {}),
      },
    },
  );
  return data;
};

/** Max establishments loaded per request in student/session create forms (security limit). */
export const ASSIGNMENT_SCOPE_PAGE_SIZE = 10;

/** Large exports (60k+ rows) can exceed default API client timeout. */
const EXPORT_EXCEL_TIMEOUT_MS = 600_000;

/** `GET /companies/current/establishments?exportExcel=true` — download XLSX. */
export const exportEstablishmentsExcel = async (
  params?: Omit<EstablishmentListParams, 'page' | 'pageSize'>,
): Promise<void> => {
  const queryParams = new URLSearchParams();
  queryParams.append('exportExcel', 'true');
  appendEstablishmentFilters(queryParams, params);

  const response = await apiClient.get(
    `/companies/current/establishments?${queryParams.toString()}`,
    { responseType: 'blob', timeout: EXPORT_EXCEL_TIMEOUT_MS },
  );

  const blob = new Blob([response.data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `establishments-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

/** `GET /companies/current/rooms` — optional `establishmentId` filter. */
export const getClassrooms = async (establishmentId?: string): Promise<Classroom[]> => {
  const qs = establishmentId ? `?establishmentId=${encodeURIComponent(establishmentId)}` : '';
  const { data } = await apiClient.get(`/companies/current/rooms${qs}`);
  return data;
};

/** `GET /contact-functions` */
export const getContactFunctions = async (): Promise<ContactFunction[]> => {
  const { data } = await apiClient.get('/contact-functions');
  return data;
};

/** `GET /academic/school-classes` */
export const getSchoolClasses = async (params?: {
  establishmentId?: string;
  schoolYearId?: string;
  levelId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<{ data: SchoolClass[]; total: number; page: number; pageSize: number }> => {
  const searchParams = new URLSearchParams();
  if (params?.establishmentId) searchParams.append('establishmentId', params.establishmentId);
  if (params?.schoolYearId) searchParams.append('schoolYearId', params.schoolYearId);
  if (params?.levelId) searchParams.append('levelId', params.levelId);
  if (params?.search) searchParams.append('search', params.search);
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  const query = searchParams.toString();
  const url = query ? `/academic/school-classes?${query}` : '/academic/school-classes';
  const { data } = await apiClient.get(url);
  return data;
};
